import type { CustomerCatalogueItemMerchandising } from "@shared-core/data/customer-api-types";

// What the item is: a description (one paragraph per non-empty line) and the
// options the item fixes and highlights, as label/value rows. Renders nothing
// when the item has neither.
export function AboutThisProduct({
  description,
  highlights,
}: {
  description: CustomerCatalogueItemMerchandising["description"];
  highlights: CustomerCatalogueItemMerchandising["highlights"];
}) {
  const paragraphs = (description ?? "")
    .split("\n")
    .map((line) => line.trim())
    .filter((line) => line.length > 0);
  const highlighted = highlights ?? [];
  if (paragraphs.length === 0 && highlighted.length === 0) return null;
  return (
    <section aria-labelledby="about-this-product" className="space-y-3">
      <h2 id="about-this-product" className="text-lg font-semibold">
        About this product
      </h2>
      {paragraphs.map((paragraph, index) => (
        <p
          key={index}
          className="text-sm leading-relaxed text-muted-foreground"
        >
          {paragraph}
        </p>
      ))}
      {highlighted.length > 0 && (
        <dl className="divide-y rounded-lg border text-sm">
          {highlighted.map((option) => (
            <div
              key={option.label}
              className="grid grid-cols-[7rem_1fr] gap-3 px-4 py-2.5"
            >
              <dt className="text-muted-foreground">{option.label}</dt>
              <dd className="font-medium">{option.value}</dd>
            </div>
          ))}
        </dl>
      )}
    </section>
  );
}

/** Whether `AboutThisProduct` would render anything, so the page can lay out around it. */
export function hasAboutContent(
  merchandising: CustomerCatalogueItemMerchandising | null | undefined,
): boolean {
  if (!merchandising) return false;
  return (
    (merchandising.description ?? "").trim().length > 0 ||
    (merchandising.highlights ?? []).length > 0
  );
}
